import React, { useState, useEffect } from 'react';

// 1. Go UP one level (../) to find the components
import HolsterVisualizer from '../HolsterVisualizer';
import ColorSelector from '../ColorSelector';
import OptionSelector from '../OptionSelector';
import SegmentedControl from '../SegmentedControl';
import useConfigShare from '../useConfigShare';
import ShareModal from '../ShareModal';

// --- IMPORTS FOR REUSABLE COMPONENTS ---
import DisclaimerBox from '../DisclaimerBox'; 
import CollapsibleSection from '../CollapsibleSection';

// 2. Import Global Settings (UPDATED WITH LEAD TIME CONSTANTS)
import { 
  ENABLE_RUSH_SHIPPING, 
  RUSH_PRICE, 
  GLOBAL_DISCOUNT_PERCENT, 
  SALE_NAME,
  QS_DAYS, 
  RUSH_DAYS, 
  CUSTOM_DAYS,  
  QS_TIMELINE_TEXT, 
  RUSH_TIMELINE_TEXT, 
  CUSTOM_TIMELINE_TEXT,
  // Lead time labels + colors
  QS_LABEL, CUSTOM_LABEL, QS_COLOR, CUSTOM_COLOR, RUSH_COLOR, leadTimeTint,
  optionLine,
  NO_CANT_IDS,
} from '../config/globalSettings';

// 3. Point to the data file
import { holsterData } from '../data/iwbHolsterData'; 
import GunSearch from '../GunSearch';

// 4. CSS
import '../App.css';


// ==========================================
// APP COMPONENT STARTS HERE
// ==========================================
function IWBBuilder() {
  // --- STATE MANAGEMENT ---
  const [viewSide, setViewSide] = useState("front"); 
  const [handId, setHandId] = useState("right");
  const [brandId, setBrandId] = useState(""); 
  const [gunId, setGunId] = useState("");
  const [isLightbearing, setIsLightbearing] = useState("no"); 
  const [lightId, setLightId] = useState("");
  const [colorId, setColorId] = useState("");
  const [mountId, setMountId] = useState("");
  const [clawId, setClawId] = useState("");
  
  // Mag Carrier State
  const [magId, setMagId] = useState("");
  const [magMountId, setMagMountId] = useState(""); 

  // --- PRE-SELECTED DEFAULTS ---
  const [cantId, setCantId] = useState("no_cant"); // <-- MATCHES DATA FILE ("no_cant")
  const [breakthroughId, setBreakthroughId] = useState("");
  const [rushService, setRushService] = useState(false);

  // ==============================================================================
  // SHARE & SAVE FUNCTIONALITY
  // ==============================================================================
  const {
    getConfigFromURL, 
    generateShareableURL, 
    saveToLocalStorage, 
    loadFromLocalStorage,
    savedConfigs, 
    saveNamedConfig, 
    deleteNamedConfig, 
    copyToClipboard, 
    copySuccess,
    shareModalOpen, 
    setShareModalOpen,
  } = useConfigShare();

  const getCurrentConfig = () => ({
    handId, brandId, gunId, isLightbearing, lightId, colorId, mountId, clawId, 
    magId, magMountId, cantId, breakthroughId, rushService,
  });

  // --- SAFE LOAD FUNCTION ---
  const loadConfig = (incomingData) => {
    if (!incomingData || incomingData.type === 'click' || incomingData._reactName) {
        return;
    }

    try {
        const config = incomingData.config ? incomingData.config : incomingData;

        setHandId(config.handId || 'right');
        setBrandId(config.brandId || '');
        setGunId(config.gunId || '');
        
        const safeLightbearing = config.isLightbearing || 'no';
        setIsLightbearing(safeLightbearing);
        setLightId(safeLightbearing === 'yes' ? (config.lightId || '') : '');

        setColorId(config.colorId || '');
        setMountId(config.mountId || '');
        setClawId(config.clawId || '');
        
        setMagId(config.magId || '');
        setMagMountId(config.magMountId || '');
        
        setCantId(config.cantId || 'no_cant'); // <-- MATCHES DATA FILE ("no_cant")
        
        setBreakthroughId(config.breakthroughId || '');
        setRushService(config.rushService === true);

        setShareModalOpen(false);
    } catch (error) {
        console.error("CRITICAL ERROR loading build:", error);
    }
  };

  useEffect(() => {
    const urlConfig = getConfigFromURL();
    if (urlConfig) {
      loadConfig(urlConfig);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    const savedConfig = loadFromLocalStorage();
    if (savedConfig) { loadConfig(savedConfig); }
  }, []);

  useEffect(() => {
    if (gunId) saveToLocalStorage(getCurrentConfig());
  }, [handId, brandId, gunId, isLightbearing, lightId, colorId, mountId, clawId, magId, magMountId, cantId, breakthroughId, rushService]);

  const shareableURL = generateShareableURL(getCurrentConfig());

  // --- FILTERING ---
  const availableGuns = brandId 
  ? holsterData.guns.filter(g => {
      if (g.brand_id !== brandId) return false;
      return true;
    })
  : [];

  // --- HELPERS ---
  const selectedGun = holsterData.guns.find(g => g.id === gunId);
  const selectedColor = holsterData.colors.find(c => c.id === colorId);
  const selectedMount = holsterData.mounts.find(m => m.id === mountId);
  const selectedClaw = holsterData.claws.find(c => c.id === clawId);
  const selectedMag = holsterData.mag_carriers?.find(m => m.id === magId);
  const selectedMagMount = holsterData.mag_mounts?.find(m => m.id === magMountId);
  const selectedCant = holsterData.cants.find(c => c.id === cantId);
  const selectedBreakthrough = holsterData.breakthrough.find(b => b.id === breakthroughId);

  // ==============================================================================
  // HAND RESTRICTION
  // no_left_hand: model cannot be produced left-handed at any lead time (no mold /
  // no reference firearm). Distinct from right_hand_only, which only means RH is
  // quick-ship while LH remains available as a custom build.
  // ==============================================================================
  const handLocked = !!selectedGun?.no_left_hand;
  const availableHands = handLocked
    ? holsterData.hands.filter(h => h.id === "right")
    : holsterData.hands;

  // ==============================================================================
  // LIGHTBEARING HAND RESTRICTION
  // Some models (e.g. FN 509 CC/LS EDGE) can only be produced as lightbearing in a
  // right-hand configuration. Non-lightbearing remains available in both hands.
  // ==============================================================================
  const lbBlocked = !!selectedGun?.lb_right_hand_only && handId === "left";

  // ==============================================================================
  // HAND-AWARE LIGHT LIST
  // supported_lights_lh, where a model defines it, replaces supported_lights for a
  // left-hand build (the OWB and Daisho builders already work this way). Used for
  // lights we only tool right-handed - e.g. XC2 / XVL2 / TLR-2 HL on the Glock
  // 19, 17, 22 and 34/35. Everything downstream reads gunLights, not the raw field.
  // ==============================================================================
  const gunLights = (handId === "left" && selectedGun?.supported_lights_lh)
    ? selectedGun.supported_lights_lh
    : (selectedGun?.supported_lights || []);
  
  // --- CONDITIONAL LOGIC FOR MAG CARRIER MOUNT ---
  const showMagMount = magId !== "" && magId !== "no";

  useEffect(() => {
      if (!showMagMount) {
          setMagMountId("");
      }
  }, [showMagMount]);

  // --- DYNAMIC MAG MOUNT MAPPING FOR DROPDOWN UI ---
  const dynamicMagMounts = (holsterData.mag_mounts || []).map(mount => ({
      ...mount,
      name: magId === "yes_2" ? `2x ${mount.name}` : mount.name,
      price: magId === "yes_2" ? mount.price * 2 : mount.price
  }));


  // --- AUTO-RESET LIGHTBEARING STATE ---
  useEffect(() => {
    if (selectedGun) {
      if (gunLights.length === 0) {
        setIsLightbearing("no");
        setLightId("");
      }
    }
  }, [gunId, handId, selectedGun]);

  // --- FORCE RIGHT HAND ON MODELS THAT CANNOT BE BUILT LEFT-HANDED ---
  // Catches selecting a locked model while LH is active, and sanitizes share URLs
  // / saved configs carrying a left-hand build for a locked model.
  useEffect(() => {
    if (handLocked && handId !== "right") {
      setHandId("right");
    }
  }, [gunId, handLocked, handId]);

  // --- AUTO-CLEAR LIGHTBEARING WHEN HAND RESTRICTION APPLIES ---
  // Catches the sequence: pick right hand -> add light -> switch to left hand.
  // Also sanitizes share URLs / saved configs that carry an invalid combination.
  useEffect(() => {
    if (lbBlocked) {
      setIsLightbearing("no");
      setLightId("");
    }
  }, [gunId, handId, lbBlocked]);

  // --- AUTO-CLEAR A LIGHT THIS HAND DOES NOT OFFER ---
  // Catches: right hand -> pick XC2 -> switch to left hand, where XC2 is not
  // offered. Also sanitizes share URLs / saved configs carrying that pair.
  useEffect(() => {
    if (lightId && !gunLights.includes(lightId)) {
      setLightId("");
    }
  }, [gunId, handId, lightId]);


  // --- FILTERING: LIGHTS ---
  // Each option carries its own lead time as a suffix, so the customer sees the
  // consequence of the choice before making it rather than after. Same source of
  // truth as the lead time box below: the gun's quick-ship arrays for this hand.
  const qsLightsThisHand = handId === "left"
    ? (selectedGun?.qs_lb_lh || [])
    : (selectedGun?.qs_lb_rh || []);

  const availableLights = isLightbearing === "yes" && selectedGun && !lbBlocked
    ? holsterData.lights
        .filter(light => gunLights.includes(light.id))
        .map(light => ({
          ...light,
          // Short forms, derived from the globals so they cannot drift:
          // "9-15 Business Days" -> "Ships 9-15 Days". Keeps the option label
          // readable in a native dropdown on a phone.
          suffix: qsLightsThisHand.includes(light.id)
            ? `Ships ${QS_TIMELINE_TEXT.replace(" Business", "")}`
            : `Custom ${CUSTOM_TIMELINE_TEXT}`,
        }))
    : [];

  const selectedLight = holsterData.lights.find(l => l.id === lightId);


  // ==============================================================================
  // LEAD TIME LOGIC — Uses gun data properties as the single source of truth.
  // ==============================================================================
  const calculateLeadTime = () => {
    if (!selectedGun || !handId) return { text: "Select Options", date: "...", type: "PENDING" };
    if (isLightbearing === "yes" && !selectedLight) return { text: "Select Light", date: "...", type: "PENDING" };

    let isQuickShip = false;

    if (isLightbearing === "no") {
      if (handId === "right") {
        isQuickShip = !!selectedGun.qs_nlb_rh;
      } else {
        isQuickShip = !!selectedGun.qs_nlb_lh;
      }
    } else if (isLightbearing === "yes" && lightId) {
      if (handId === "right") {
        isQuickShip = Array.isArray(selectedGun.qs_lb_rh) && selectedGun.qs_lb_rh.includes(lightId);
      } else {
        isQuickShip = Array.isArray(selectedGun.qs_lb_lh) && selectedGun.qs_lb_lh.includes(lightId);
      }
    }

    const qsDays = QS_DAYS;       
    const rushDays = RUSH_DAYS;    
    const customDays = CUSTOM_DAYS;  
    
    let targetDays = customDays;
    if (isQuickShip) {
        targetDays = qsDays;
    } else if (rushService) {
        targetDays = rushDays;
    }
    
    const date = new Date();
    let daysAdded = 0;
    while (daysAdded < targetDays) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) daysAdded++;
    }
    const dateString = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    if (isQuickShip) {
        return { type: QS_LABEL, timeline: QS_TIMELINE_TEXT, date: dateString, color: QS_COLOR, bg: leadTimeTint(QS_COLOR), isCustom: false };
    } 
    if (rushService && ENABLE_RUSH_SHIPPING) {
        return { type: "RUSH PRIORITY", timeline: RUSH_TIMELINE_TEXT, date: dateString, color: RUSH_COLOR, bg: leadTimeTint(RUSH_COLOR), isCustom: true };
    }
    return { type: CUSTOM_LABEL, timeline: CUSTOM_TIMELINE_TEXT, date: dateString, color: CUSTOM_COLOR, bg: leadTimeTint(CUSTOM_COLOR), isCustom: true };
  };

  const leadTimeInfo = calculateLeadTime();

  // --- AUTO-CLEAR RUSH WHEN NOT APPLICABLE ---
  useEffect(() => {
      if (rushService && (!leadTimeInfo.isCustom || leadTimeInfo.type === QS_LABEL)) {
          setRushService(false);
      }
  }, [gunId, handId, lightId, isLightbearing]);
  

  // --- SMART WARNINGS ---
  let expertWarning = null;
  if (gunId && gunId.includes("glock") && lightId && lightId.includes("x300")) expertWarning = "EXPERT NOTE: The Surefire X300 Ultra extends past the muzzle of the Glock 19. The holster will be cut flush with the light, leaving a small gap around the muzzle. This is normal.";
  if (gunId && gunId.includes("p365") && lightId === "tlr7") expertWarning = "EXPERT NOTE: The TLR-7 is wider than the P365 frame. There will be a slight gap in the trigger guard area to accommodate the light body.";
  if (gunId === "iwi_masada_slim") expertWarning = "IMPORTANT: The IWI Masada Slim Commander model does not fit our holsters. This holster is designed for the standard Masada Slim only.";
  if (gunId === "sw_bodyguard_2") expertWarning = "NOTE: The Bodyguard 2.0 shell is intentionally longer than the firearm to aid in concealment. The extended shell helps prevent the grip from protruding outward and printing.";

  // --- DYNAMIC PRICING LOGIC ---
  const BASE_PRICE = 58.99;
  
  let activeMagMountPrice = 0;
  if (showMagMount && selectedMagMount) {
      activeMagMountPrice = magId === "yes_2" ? (selectedMagMount.price * 2) : selectedMagMount.price;
  }

  const LIGHTBEARING_UPCHARGE = 9.99;

  const subtotal = 
    BASE_PRICE +
    (isLightbearing === "yes" ? LIGHTBEARING_UPCHARGE : 0) +
    (selectedColor?.price || 0) +
    (selectedMount?.price || 0) +
    (selectedClaw?.price || 0) +
    (selectedMag?.price || 0) +
    activeMagMountPrice +
    (selectedCant?.price || 0) +
    (selectedBreakthrough?.price || 0) +
    (rushService && ENABLE_RUSH_SHIPPING ? RUSH_PRICE : 0);

  const discountAmount = subtotal * GLOBAL_DISCOUNT_PERCENT;
  const total = (subtotal - discountAmount).toFixed(2);


  // --- VALIDATION HELPER ---
  let isReady = 
    brandId && 
    gunId && 
    colorId && 
    mountId && 
    clawId && 
    magId && 
    cantId &&
    breakthroughId && 
    (isLightbearing === "no" || (isLightbearing === "yes" && lightId));

  if (showMagMount && !magMountId) {
      isReady = false;
  }

  // Hard block: never allow a left-hand lightbearing build on a restricted model.
  if (lbBlocked && isLightbearing === "yes") {
      isReady = false;
  }

  // Hard block: never allow a left-hand build on a model that cannot be built LH.
  if (handLocked && handId !== "right") {
      isReady = false;
  }

  // --- ADD TO CART FUNCTION ---
  const handleAddToCart = () => {
    if (!isReady) return;

    let magDetails = null;
    if (showMagMount) {
        const mountNameStr = magId === "yes_2" ? `2x ${selectedMagMount?.name}` : selectedMagMount?.name;
        magDetails = `Mag Carrier: ${selectedMag?.name}\n   - Mag Mount: ${mountNameStr}`;
    } else {
        // Declined: leave it off the ticket entirely.
        magDetails = null;
    }

    const configuration = [
        `Gun Model: ${selectedGun?.name}`,
        `Hand: ${handId === 'right' ? 'Right' : 'Left'}`,
        isLightbearing === "yes" ? `Weapon Light: ${selectedLight?.name}` : "Lightbearing: No",
        `Color: ${selectedColor?.name}`,
        `Mount: ${selectedMount?.name}`,
        optionLine("Claw", selectedClaw?.name),
        NO_CANT_IDS.includes(cantId) ? null : `Cant: ${selectedCant?.name}`,
        magDetails, 
        optionLine("Breakthrough Clean", selectedBreakthrough?.name),
        `Warranty: Free Lifetime Warranty`,
        `Lead Time: ${leadTimeInfo.timeline}`,
    ].filter(Boolean).join('\n');

    const productID = 283902; 
    const params = new URLSearchParams();
    params.append('add-to-cart', productID);
    params.append('quantity', '1');
    params.append('anr_config', configuration);
    params.append('anr_price', total);

    const addToCartURL = `/checkout/?${params.toString()}`;
    window.location.href = addToCartURL;
  };

  return (
    <div className={`builder-container ${colorId ? "sticky-active" : ""}`}>
      <div className="controls-section">
        <h1 className="header-title">Inside the Waistband Builder <br/><span style={{fontSize:"16px", color:"#D5A678"}}>Concealed Carry Holster Configurator</span></h1>
        
        {/* FEATURES & LEAD TIME BOX */}
        <div style={{ color: "#aaa", fontSize: "14px", lineHeight: "1.7", marginBottom: "40px" }}>
           <h3 style={{ border: "none", padding: 0, color: "#fff", textTransform: "uppercase" }}>Features</h3>
           <ul style={{ paddingLeft: "20px", marginBottom: "20px", listStyleType: "disc" }}>
             <li><strong style={{ color: "#fff" }}>KYDEX® Thermoplastics Construction:</strong> Durable, lightweight, and sweat-resistant.</li>
             <li><strong style={{ color: "#fff" }}>Full Trigger Guard Protection:</strong> Ensures a safe and secure carry.</li>
             <li><strong style={{ color: "#fff" }}>Perfect Retention:</strong> Adjustable retention allows customization.</li>
             <li><strong style={{ color: "#fff" }}>Optional Concealment Claw:</strong> Superior concealment.</li>
             <li><strong style={{ color: "#fff" }}>Optic & Suppressor Ready:</strong> Compatible with most optics/sights up to 0.450″.</li>
             <li><strong style={{ color: "#fff" }}>Manufactured in New Hampshire:</strong> Materials Sourced in the USA.</li>
           </ul>
           <p><strong>NSN#:</strong> 1095-01-699-8795</p>
        </div>

        {/* --- FORM INPUTS --- */}
        <SegmentedControl 
          label="Dominant Hand" 
          options={availableHands} 
          selectedValue={handId} 
          onChange={(val) => { 
              setHandId(val); 
          }} 
        />

        {handLocked && (
          <div style={{ marginTop: "-10px", marginBottom: "20px", fontSize: "13px", color: "#D5A678" }}>
              This model is available in a right-hand configuration only.
          </div>
        )}
        
        <GunSearch
          guns={holsterData.guns}
          brands={holsterData.brands}
          onPick={(gun) => { setBrandId(gun.brand_id); setGunId(gun.id); }}
        />

        <OptionSelector label="Select Manufacturer *" options={holsterData.brands} selectedValue={brandId} onChange={(val) => { setBrandId(val); setGunId(""); }} />
        <OptionSelector label="Gun Model *" options={availableGuns} selectedValue={gunId} onChange={setGunId} disabled={!brandId} />
        
        {!lbBlocked && (!selectedGun || gunLights.length > 0) && (
          <div className="fade-in-section">
            <SegmentedControl 
                label="Weapon Light" 
                options={[
                    { id: "no", name: "Non-Lightbearing" },
                    { id: "yes", name: "Lightbearing (+$9.99)" }
                ]} 
                selectedValue={isLightbearing} 
                onChange={(val) => {
                    setIsLightbearing(val);
                    setLightId(""); 
                }} 
            />  
          </div>
        )}

        {/* --- LIGHTBEARING RESTRICTED TO RIGHT HAND ON THIS MODEL --- */}
        {lbBlocked && (
          <div style={{ marginTop: "15px", marginBottom: "20px", padding: "15px", backgroundColor: "#1e1e1e", border: "1px solid #444", borderRadius: "4px", fontSize: "14px", color: "#ccc" }}>
              <strong style={{color: "#D5A678", display: "block", marginBottom: "5px"}}>Lightbearing Available in Right Hand Only</strong>
              <p style={{margin: 0, fontSize: "13px"}}>This model is only produced as a lightbearing holster in a right-hand configuration. Switch your dominant hand to Right to add a weapon light, or continue with a left-hand non-lightbearing build.</p>
          </div>
        )}

        {selectedGun && gunLights.length === 0 && (
          <div style={{ marginTop: "15px", marginBottom: "20px", padding: "15px", backgroundColor: "#1e1e1e", border: "1px solid #444", borderRadius: "4px", fontSize: "14px", color: "#ccc" }}>
              <strong style={{color: "#D5A678", display: "block", marginBottom: "5px"}}>No Lightbearing Models Available</strong>
              <p style={{margin: "0 0 10px 0", fontSize: "13px"}}>We do not currently offer a standard lightbearing option for this specific firearm model.</p>
              <a href="https://www.anrkydexholsters.com/custom-holster-request/" target="_blank" rel="noopener noreferrer" style={{color: "#D5A678", textDecoration: "underline", fontSize: "13px", fontWeight: "bold"}}>Custom Holster Request | ANR Design Holsters</a>
          </div>
        )}
        
        {isLightbearing === "yes" && !lbBlocked && (
            <OptionSelector label="Select Weapon Light *" options={availableLights} selectedValue={lightId} onChange={setLightId} />
        )}

        {expertWarning && <div style={{marginTop:"-15px", marginBottom:"25px", padding:"15px", backgroundColor:"rgba(213, 166, 120, 0.15)", borderLeft:"3px solid #D5A678", color:"#D5A678", fontSize:"13px", lineHeight:"1.5"}}>{expertWarning}</div>}

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>
        
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
          <h3 style={{ textTransform: "uppercase", margin: 0 }}>Color Options</h3>
          <div className="tooltip-wrapper">
             <span className="info-icon">i</span>
             <div className="tooltip-content">
                ANR Design does not have control over the pattern. The model portrayed in the configurator is a representation.
             </div>
          </div>
        </div>

        <ColorSelector options={holsterData.colors} selectedValue={colorId} onChange={setColorId} />
        
        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
          <h3 style={{ textTransform: "uppercase", margin: 0 }}>Mounting Options</h3>
          <div className="tooltip-wrapper">
              <span className="info-icon">i</span>
              <div className="tooltip-content">
                <div className="mega-tooltip">
                  {holsterData.mounts.map(mount => (
                      <div key={mount.id} className="mount-info-row" style={{marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #555'}}>
                          <div className="mount-text">
                              <h4 style={{marginTop: 0, marginBottom: '5px'}}>{mount.name}</h4>
                              <p style={{margin: 0}}>{mount.description}</p>
                          </div>
                      </div>
                  ))}
                </div>
              </div>
          </div>
        </div>

        <OptionSelector label="Mounting Selection *" options={holsterData.mounts} selectedValue={mountId} onChange={setMountId} />

        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "30px", marginBottom: "10px" }}>
           <h3 style={{ textTransform: "uppercase", margin: 0 }}>Concealment Claw</h3>
           <div className="tooltip-wrapper">
              <span className="info-icon">i</span>
              <div className="tooltip-content">
                  Injection molded accessory designed to reduce printing while concealed carrying. The claw tucks behind your pant seam which helps keep the grip of the pistol from protruding.
              </div>
           </div>
        </div>
        <OptionSelector label="Concealment Claw" options={holsterData.claws} selectedValue={clawId} onChange={setClawId} />

        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "30px", marginBottom: "10px" }}>
           <h3 style={{ textTransform: "uppercase", margin: 0 }}>Holster Cant</h3>
           <div className="tooltip-wrapper">
              <span className="info-icon">i</span>
              <div className="tooltip-content">
                  Gun cant will provide a slight 10 degree angle to your holster. This may be advantageous if carrying near the 4 o'clock position.
              </div>
           </div>
        </div>
        <SegmentedControl label="" options={holsterData.cants} selectedValue={cantId} onChange={setCantId} />

        {/* --- FREE LIFETIME WARRANTY BANNER --- */}
        <div style={{ marginTop: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <h3 style={{ textTransform: "uppercase", margin: 0 }}>Lifetime Warranty</h3>
                <div className="tooltip-wrapper">
                    <span className="info-icon">i</span>
                    <div className="tooltip-content">
                        Every ANR Design holster is backed by an automatic Lifetime Warranty on the Kydex® shell for the original purchaser. If your shell cracks or breaks under normal, intended use, we replace it. Consumables (hardware, loops, spacers), cosmetic wear, and damage from modifications or extreme heat are not covered. Return shipping fees apply.
                        <br/><br/>
                        <a href="https://www.anrkydexholsters.com/warranty-privacy-and-shipping-policy/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                            Learn More & File a Claim
                        </a>
                    </div>
                </div>
            </div>
            <div style={{
                padding: "15px",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                borderLeft: "4px solid #4caf50",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#4caf50",
                fontWeight: "600"
            }}>
                ✓ Free Lifetime Warranty Included
            </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "30px", marginBottom: "15px" }}>
           <h3 style={{ textTransform: "uppercase", margin: 0 }}>Add Matching Mag Carrier</h3>
           <div className="tooltip-wrapper">
              <span className="info-icon">i</span>
              <div className="tooltip-content">
                  Standard Pistol Magazine Carrier, matching your holster color.
              </div>
           </div>
        </div>
        <OptionSelector label="Add a Magazine Carrier *" options={holsterData.mag_carriers} selectedValue={magId} onChange={setMagId} />

        {/* DYNAMIC MAG MOUNT */}
        {showMagMount && (
            <div className="fade-in-section" style={{marginLeft: "15px", borderLeft: "2px solid #D5A678", paddingLeft: "15px", marginTop: "15px"}}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                    <h4 style={{ color: "#D5A678", marginTop: "0", marginBottom: "0", fontSize: "14px", textTransform: "uppercase" }}>Select Mount for Magazine Carrier</h4>
                </div>
                <OptionSelector 
                    label="Mag Mount *" 
                    options={dynamicMagMounts} 
                    selectedValue={magMountId} 
                    onChange={setMagMountId} 
                />
            </div>
        )}

        <h3 style={{ textTransform: "uppercase", marginTop: "30px" }}>Add Breakthrough Products</h3>
        <p style={{ fontSize: "14px", color: "#666", marginBottom: "10px" }}>Optional firearm maintenance cleaning solvents.</p>
        <OptionSelector label="Optional add-on firearm maintenance" options={holsterData.breakthrough} selectedValue={breakthroughId} onChange={setBreakthroughId} />

        <hr style={{margin:"50px 0", borderTop:"1px solid #333"}}/>

        <CollapsibleSection title="Description">
            <p style={{ marginTop: 0 }}>
                Securely and discreetly carry your firearm with our <strong>minimalist KYDEX® Thermoplastics IWB Holster</strong>. 
                Precision CNC cut and vacuum formed for a perfect fit.
            </p>
            <p style={{ color: "#D5A678", fontWeight: "bold", marginBottom: 0 }}>
                All ANR Design holsters come suppressor sight ready up to 0.450″ tall.
            </p>
        </CollapsibleSection>
        
        <div className="mobile-only-content">
            <CollapsibleSection title="Important Notes & Warnings">
                <DisclaimerBox mode="mobile" setShareModalOpen={setShareModalOpen} gunId={gunId} />
            </CollapsibleSection>
        </div>
      </div>

      <div className="preview-section">
        <div className="sticky-wrapper">

          <HolsterVisualizer 
            holsterType="iwb" 
            gunData={selectedGun} 
            lightData={selectedLight} 
            colorData={selectedColor} 
            mountData={selectedMount} 
            clawData={selectedClaw} 
            cantId={cantId}
            isLightbearing={isLightbearing} 
            isLeftHanded={handId === "left"} 
            viewSide={viewSide} 
          />

          {/* --- FRONT / REAR TOGGLE --- */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", marginBottom: "15px" }}>
            <div style={{ display: "flex", backgroundColor: "#333", borderRadius: "4px", padding: "4px", gap: "5px" }}>
              <button onClick={() => setViewSide("front")} style={{ background: viewSide === "front" ? "#D5A678" : "transparent", color: viewSide === "front" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>FRONT</button>
              <button onClick={() => setViewSide("back")} style={{ background: viewSide === "back" ? "#D5A678" : "transparent", color: viewSide === "back" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>REAR</button>
            </div>
          </div>

          {leadTimeInfo.type !== "PENDING" && (
             <div className="lead-time-box" style={{ 
                 backgroundColor: leadTimeInfo.bg, 
                 padding: "15px", 
                 borderLeft: `4px solid ${leadTimeInfo.color}`, 
                 marginBottom: "20px"
             }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                    <div>
                        {leadTimeInfo.type && <strong style={{ color: leadTimeInfo.color, textTransform: "uppercase", fontSize: "16px" }}>{leadTimeInfo.type}</strong>}
                        <div style={{ marginTop: "5px", color: "#fff" }}>
                        Lead Time: <strong style={{ color: leadTimeInfo.color }}>{leadTimeInfo.timeline}</strong><br/>
                        Est. Ship Date: <strong>{leadTimeInfo.date}</strong>
                        </div>
                    </div>
                    
                    {leadTimeInfo.isCustom && ENABLE_RUSH_SHIPPING && (
                        <div style={{ marginLeft: "15px" }}>
                            <label style={{ 
                                display: "flex", alignItems: "center", cursor: "pointer",
                                background: rushService ? "#D5A678" : "transparent",
                                border: "1px solid #d32f2f", padding: "12px 20px", borderRadius: "4px",
                                transition: "all 0.2s"
                            }}>
                                <input 
                                    type="checkbox" 
                                    checked={rushService} 
                                    onChange={(e) => setRushService(e.target.checked)}
                                    style={{ marginRight: "8px", accentColor: "#000" }} 
                                />
                                <div style={{ lineHeight: "1.7" }}>
                                    <div style={{ fontSize: "15px", fontWeight: "bold", color: rushService ? "#000" : "#D5A678", textTransform: "uppercase" }}>RUSH MY BUILD</div>
                                    <div style={{ fontSize: "15px", color: rushService ? "#000" : "#ccc" }}>+${RUSH_PRICE.toFixed(2)}</div>
                                </div>
                            </label>
                        </div>
                    )}
                </div>
             </div>
          )}
          {leadTimeInfo.type === "PENDING" && (
            <div style={{ textAlign: "center", marginTop: "15px", color: "#666", fontSize: "12px", marginBottom: "15px" }}>Select options to see lead time</div>
          )}
          
          <div className="price-tag">
            {GLOBAL_DISCOUNT_PERCENT > 0 ? (
                <>
                <span style={{ textDecoration: "line-through", color: "#888", marginRight: "10px", fontSize: "0.8em" }}>
                    ${subtotal.toFixed(2)}
                </span>
                <span style={{ color: "#00ff00" }}>
                    ${total}
                </span>
                <div style={{ fontSize: "10px", color: "#00ff00", textTransform: "uppercase", letterSpacing: "1px", marginTop: "5px" }}>
                    {SALE_NAME} ACTIVE
                </div>
                </>
            ) : (
                `Total: $${total}`
            )}
          </div>

          <DisclaimerBox mode="desktop" setShareModalOpen={setShareModalOpen} gunId={gunId} />

          <button className={`add-to-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>
            {isReady ? "ADD TO CART" : "SELECT OPTIONS"}
          </button>
        </div>
      </div>
      
      <div className="mobile-action-bar mobile-only-content">
        <div className="mobile-price-container">
          <span className="mobile-label">Estimated Total</span>
          <span className="mobile-total">${total}</span>
        </div>
        <button 
          className={`mobile-cart-btn ${isReady ? 'pulse-active' : ''}`} 
          disabled={!isReady} 
          onClick={handleAddToCart}
        >
           {isReady ? "ADD TO CART" : "INCOMPLETE"}
        </button>
      </div>

      <ShareModal 
        isOpen={shareModalOpen} 
        onClose={() => setShareModalOpen(false)} 
        shareableURL={shareableURL} 
        currentConfig={getCurrentConfig()} 
        savedConfigs={savedConfigs} 
        saveNamedConfig={saveNamedConfig} 
        loadConfig={loadConfig} 
        deleteNamedConfig={deleteNamedConfig} 
        copyToClipboard={copyToClipboard} 
        copySuccess={copySuccess} 
        gunName={selectedGun?.name} 
        lightName={selectedLight?.name}
        colorName={selectedColor?.name} 
      />
    </div>
  );
}

export default IWBBuilder;